import { useEffect, useState, RefObject } from 'react';

interface TocItem {
    id: string;
    text: string;
    level: number;
}

function slugify(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-');
}

interface TableOfContentsProps {
    targetRef: RefObject<HTMLElement>;
}

export default function TableOfContents({ targetRef }: TableOfContentsProps) {
    const [items, setItems] = useState<TocItem[]>([]);

    useEffect(() => {
        const container = targetRef.current;
        if (!container) return;

        const headings = Array.from(container.querySelectorAll('h2, h3')) as HTMLElement[];
        const used = new Set<string>();
        const built: TocItem[] = headings.map((heading) => {
            const base = slugify(heading.textContent || '') || 'section';
            let slug = base;
            let suffix = 2;
            while (used.has(slug)) {
                slug = `${base}-${suffix}`;
                suffix += 1;
            }
            used.add(slug);
            if (!heading.id) heading.id = slug;
            return { id: heading.id, text: heading.textContent || '', level: heading.tagName === 'H3' ? 3 : 2 };
        });

        setItems(built);
    }, [targetRef]);

    if (items.length < 3) return null;

    return (
        <nav aria-label="Table of contents" className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="mb-2 text-sm font-semibold text-gray-900">On this page</p>
            <ul className="space-y-1 text-sm">
                {items.map((item) => (
                    <li key={item.id} className={item.level === 3 ? 'ml-4' : undefined}>
                        <a href={`#${item.id}`} className="text-blue-700 hover:underline">
                            {item.text}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
    );
}
